import {
  type AwayStatus as CoreAwayStatus,
  defaultRules,
  type EstimateDefaults,
  formatLocalDate,
  type HeadsUp,
  type Item,
  type ItemPlan,
  localDateOf,
  PLANNING_MINUTES_MAX,
  PLANNING_MINUTES_MIN,
  PLANNING_MINUTES_STEP,
  type TimeOff,
} from '@hugo/core'
import { formatDay, formatDayRange, formatHours, formatMinutes } from './format'

/**
 * Words and labels for hugo's plan, time off and heads-ups. Dates, minutes and whether the user
 * is away come from the API (the planner in packages/insights); the default time per type is
 * the table in packages/core. These functions only put them into words. The one date worked out
 * here is today's, for labels ("Mon 26 Oct" without the year in the current year).
 */

/** The user's date today (YYYY-MM-DD in their time zone) and its year. */
export function userToday(now: Date, timeZone: string): { today: string; year: number } {
  const date = localDateOf(now.getTime(), timeZone)
  return { today: formatLocalDate(date), year: date.year }
}

/** The choices for the hours a day hugo plans for dated work: 0.5 h to 12 h. */
export const PLANNING_CHOICES: readonly number[] = Array.from(
  { length: (PLANNING_MINUTES_MAX - PLANNING_MINUTES_MIN) / PLANNING_MINUTES_STEP + 1 },
  (_, i) => PLANNING_MINUTES_MIN + i * PLANNING_MINUTES_STEP,
)

/** The choices for "Time needed", in minutes; no estimate means hugo's default. */
export const ESTIMATE_CHOICES: readonly number[] = [
  15, 30, 45, 60, 90, 120, 180, 240, 360, 480, 960, 2400,
]

/**
 * The minutes hugo assumes for an item without an estimate: the plan's when it has one, else the
 * user's default for its type (their rules; the built-in ones until settings have loaded).
 */
export function defaultMinutes(
  item: Pick<Item, 'type' | 'plan'>,
  defaults: EstimateDefaults = defaultRules.estimateMinutes,
): number {
  if (item.plan && !item.plan.estimated) return item.plan.minutes
  return item.type ? defaults[item.type] : defaults.untyped
}

/** "Default (≈ 1 h)": the "Time needed" choice that leaves it to hugo. */
export function defaultEstimateLabel(
  item: Pick<Item, 'type' | 'plan'>,
  defaults?: EstimateDefaults,
): string {
  return `Default (≈ ${formatMinutes(defaultMinutes(item, defaults))})`
}

/** Whether a row says when its work likely gets done: tasks and promises at risk or overdue. */
export function showsPlanBadge(item: Pick<Item, 'kind' | 'plan'>): boolean {
  return (
    (item.kind === 'task' || item.kind === 'owe') &&
    (item.plan?.status ?? 'on_track') !== 'on_track'
  )
}

/** "Likely Fri 13 Nov", or "Not within a year" when the work does not fit in one. */
export function planBadgeLabel(plan: Pick<ItemPlan, 'projectedOn'>, thisYear: number): string {
  return plan.projectedOn ? `Likely ${formatDay(plan.projectedOn, thisYear)}` : 'Not within a year'
}

/**
 * The plan in one to three sentences: "At 4 h a day it's done by Fri 13 Nov — after the date you
 * promised (Wed 28 Oct). Meetings take 2 h of those hours. You're away Mon 26 Oct – Fri 6 Nov."
 * Without the planning setting (still loading) it leaves out the pace.
 */
export function planSentence(
  plan: ItemPlan,
  options: { kind: 'task' | 'owe'; planningMinutes: number | null; thisYear: number },
): string {
  const day = (date: string) => formatDay(date, options.thisYear)
  const done = (rest: string) =>
    options.planningMinutes === null
      ? `It's ${rest}`
      : `At ${formatHours(options.planningMinutes)} a day it's ${rest}`
  const owe = options.kind === 'owe'
  const due = plan.dueOn ? day(plan.dueOn) : ''
  const finish = plan.projectedOn ? `done by ${day(plan.projectedOn)}` : 'not done within a year'
  let text: string
  if (plan.dueOn === null) {
    // Planned on the board without a due date: only when it gets done.
    text = `${done(finish)}.`
  } else if (plan.status === 'overdue') {
    text = `${owe ? `You promised it for ${due}.` : `It was due ${due}.`} ${done(finish)}.`
  } else if (plan.projectedOn === null) {
    text = `${done(finish)} — ${owe ? `you promised ${due}` : `it's due ${due}`}.`
  } else if (plan.status === 'at_risk') {
    text = `${done(finish)} — ${owe ? `after the date you promised (${due})` : `after it's due (${due})`}.`
  } else if (plan.projectedOn === plan.dueOn) {
    text = `${done(finish)}, the day it's due.`
  } else {
    text = `${done(finish)}, before it's due (${due}).`
  }
  // Meetings and out-of-office time (connected calendars) took from the planned hours.
  const of = options.planningMinutes === null ? 'the planned time' : 'those hours'
  const meetings = formatMinutes(plan.meetingMinutes)
  const away = formatMinutes(plan.outOfOfficeMinutes)
  if (plan.meetingMinutes > 0 && plan.outOfOfficeMinutes > 0) {
    text = `${text} Meetings take ${meetings} and out-of-office time ${away} of ${of}.`
  } else if (plan.meetingMinutes > 0) {
    text = `${text} Meetings take ${meetings} of ${of}.`
  } else if (plan.outOfOfficeMinutes > 0) {
    text = `${text} Out-of-office time in your calendar takes ${away} of ${of}.`
  }
  if (!plan.timeOff) return text
  const { startsOn, endsOn } = plan.timeOff
  return `${text} You're away ${formatDayRange(startsOn, endsOn, options.thisYear)}.`
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

/**
 * The Outbox's attention dot: "3 heads-ups to send", "1 reminder to send", "2 heads-ups and 1
 * reminder to send". Empty when there is nothing.
 */
export function toSendLabel(headsUps: number, reminders: number): string {
  const parts = [
    headsUps > 0 ? plural(headsUps, 'heads-up', 'heads-ups') : null,
    reminders > 0 ? plural(reminders, 'reminder', 'reminders') : null,
  ].filter(Boolean)
  return parts.length === 0 ? '' : `${parts.join(' and ')} to send`
}

/** The Outbox section's heading for what hugo suggests sending. */
export function toSendHeading(list: readonly Pick<HeadsUp, 'kind'>[]): string {
  const reminders = list.filter((h) => h.kind === 'waiting_followup').length
  if (reminders === 0) return 'Heads-ups to send'
  return reminders === list.length ? 'Reminders to send' : 'Heads-ups and reminders to send'
}

/** "10 working days", "1 working day", "no working days". */
export function workingDaysText(count: number): string {
  if (count === 0) return 'no working days'
  return count === 1 ? '1 working day' : `${count} working days`
}

/** A time-off period in Settings: "Mon 26 Oct – Fri 6 Nov · Vacation · 10 working days". */
export function timeOffText(
  period: Pick<TimeOff, 'startsOn' | 'endsOn' | 'label' | 'workingDays'>,
  thisYear: number,
): string {
  return [
    formatDayRange(period.startsOn, period.endsOn, thisYear),
    period.label,
    workingDaysText(period.workingDays),
  ]
    .filter(Boolean)
    .join(' · ')
}

type Days = { startsOn: string; endsOn: string }

export type TimeOffState = 'ended' | 'current' | 'upcoming'

export function timeOffState(period: Days, today: string): TimeOffState {
  if (period.endsOn < today) return 'ended'
  return period.startsOn <= today ? 'current' : 'upcoming'
}

const byDays = (a: Days, b: Days) =>
  a.startsOn.localeCompare(b.startsOn) || a.endsOn.localeCompare(b.endsOn)

/** What Settings lists: the (at most) 3 periods that ended last, then current and upcoming ones. */
export function visibleTimeOff<T extends Days>(
  periods: readonly T[],
  today: string,
  ended = 3,
): T[] {
  const past = periods
    .filter((p) => p.endsOn < today)
    .sort((a, b) => a.endsOn.localeCompare(b.endsOn))
    .slice(-ended)
  return [...past, ...periods.filter((p) => p.endsOn >= today)].sort(byDays)
}

/** Away now or soon (never null here: the header shows nothing then). */
type AwayStatus = NonNullable<CoreAwayStatus>

/** "until Fri 6 Nov" / "from Mon 26 Oct". */
export function awayWhen(status: AwayStatus, thisYear: number): string {
  return status.state === 'away'
    ? `until ${formatDay(status.until, thisYear)}`
    : `from ${formatDay(status.from, thisYear)}`
}

/** "Away until Fri 6 Nov" / "Away from Mon 26 Oct". */
export function awayLabel(status: AwayStatus, thisYear: number): string {
  return `Away ${awayWhen(status, thisYear)}`
}

/** "You're away 26 Oct – 6 Nov": a waiting-on row whose person should hear about time off. */
export function awayHint(timeOff: Days, thisYear: number): string {
  return `You're away ${formatDayRange(timeOff.startsOn, timeOff.endsOn, thisYear, { weekday: false })}`
}

export function firstName(name: string | null): string | null {
  return name?.trim().split(/\s+/)[0] || null
}

/**
 * A late promise with another date than hugo's: for a reply the server writes it into the
 * message (the client never edits the text); a copied text still says hugo's date. A date
 * earlier than hugo's plan gets a warning: it would be late again.
 */
export function newDateNote(
  channel: HeadsUp['channel'],
  suggested: string,
  chosen: string,
  thisYear: number,
): string {
  const day = (date: string) => formatDay(date, thisYear)
  const early = chosen < suggested ? `hugo's plan has it done by ${day(suggested)}. ` : ''
  return channel === 'reply'
    ? `${early}hugo writes ${day(chosen)} into the message when you add it to the Outbox.`
    : `${early}The text says ${day(suggested)}: change it to ${day(chosen)} when you send it.`
}

/** "Tell Anna" for a heads-up, "Remind Marco" for a reminder: who it is for. */
export function headsUpTitle(headsUp: Pick<HeadsUp, 'person'> & Partial<Pick<HeadsUp, 'kind'>>) {
  const first = firstName(headsUp.person)
  if (headsUp.kind === 'waiting_followup') return first ? `Remind ${first}` : 'Reminder'
  return first ? `Tell ${first}` : 'Heads-up'
}

/** "Remind Marco": a waiting-on row whose follow-up has come and who has not written since. */
export function reminderHint(person: string | null): string {
  const first = firstName(person)
  return first ? `Remind ${first}` : 'Reminder ready'
}

/**
 * "Replied Tue 20 Oct": they wrote after the follow-up date was set, so no reminders. The row
 * names the person already ("from Anna Keller"); short enough for a phone row.
 */
export function repliedHint(repliedAt: string, timeZone: string, thisYear: number): string {
  const day = formatLocalDate(localDateOf(Date.parse(repliedAt), timeZone))
  return `Replied ${formatDay(day, thisYear)}`
}

/**
 * After a reminder: "If Marco doesn't reply, hugo suggests another reminder on Mon 26 Oct." Null
 * without a next follow-up day.
 */
export function reminderNote(
  headsUp: Pick<HeadsUp, 'person' | 'newDate'>,
  thisYear: number,
): string | null {
  if (!headsUp.newDate) return null
  const first = firstName(headsUp.person)
  const who = first ? `${first} doesn't` : "they don't"
  return `If ${who} reply, hugo suggests another reminder on ${formatDay(headsUp.newDate, thisYear)}.`
}

/**
 * Why a heads-up: "Due Wed 28 Oct, new date Fri 13 Nov · you're away 26 Oct – 6 Nov" for a late
 * promise, "You're away 26 Oct – 6 Nov · follow-up Thu 29 Oct" for someone the user waits on,
 * "Follow-up was Tue 20 Oct · no reply yet" for a reminder.
 */
export function headsUpReason(
  headsUp: Pick<HeadsUp, 'kind' | 'dueOn' | 'newDate' | 'timeOff'>,
  today: string,
  thisYear: number,
): string {
  const day = (date: string) => formatDay(date, thisYear)
  const { timeOff, dueOn, newDate } = headsUp
  const away = timeOff
    ? formatDayRange(timeOff.startsOn, timeOff.endsOn, thisYear, { weekday: false })
    : null
  if (headsUp.kind === 'waiting_followup') {
    const when =
      dueOn === null
        ? 'Follow-up'
        : dueOn >= today
          ? 'Follow-up today'
          : `Follow-up was ${day(dueOn)}`
    return `${when} · no reply yet`
  }
  if (headsUp.kind === 'waiting_away') {
    return [away ? `You're away ${away}` : null, dueOn ? `follow-up ${day(dueOn)}` : null]
      .filter(Boolean)
      .join(' · ')
  }
  let when: string | null = null
  if (dueOn) {
    const due = `${dueOn < today ? 'Was due' : 'Due'} ${day(dueOn)}`
    when = newDate ? `${due}, new date ${day(newDate)}` : due
  } else if (newDate) {
    when = `New date ${day(newDate)}`
  }
  return [when, away ? `you're away ${away}` : null].filter(Boolean).join(' · ')
}
